import {FunctionComponent, MouseEvent, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMatch, useNavigate} from 'react-router';
import Arrow from '@/assets/images/icon-arrow-right.svg?react';
import HorizontalDotsIcon from '@/assets/images/icon-horizontal-dots.svg?react';
import PencilOutline from '@/assets/images/icon-pencil-outline.svg?react';
import TrashCan from '@/assets/images/icon-trash-can.svg?react';
import Button from '@/components/button';
import ConfirmDialog from '@/components/confirm-dialog';
import {useConfirmDialogActions} from '@/components/confirm-dialog/use-confirm-dialog-actions';
import Link from '@/components/link';
import PopupMenu from '@/components/popup-menu';
import {TrackedButton} from '@/components/tracked-atoms/tracked-button';
import {BUTTON_THEME, BUTTON_VARIANT} from '@/constants/button';
import {APP_PATH} from '@/constants/routes';
import {
  useCancelSignatureRequest,
  useDeleteDocument,
  useRetryFinalSubmit,
  useRetrySubmitForSignature,
} from '@/hooks/queries/documents';
import {Document} from '@/interfaces/document';
import {TrackingClickPrefix} from '@/services/analytics/analytics-types';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import ItemContent from './content';

interface ElementProps {
  linksDisabled?: boolean;
  showVersion?: boolean;
  document: Document;
  onClick: () => void;
}

const DocumentListItem: FunctionComponent<ElementProps> = ({linksDisabled, showVersion, document, onClick}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const isCurrentDocumentRoute = useMatch(`/app/form/${document.id}`);

  const {mutateAsync: cancelSignatureRequestMutation} = useCancelSignatureRequest();
  const {mutateAsync: deleteDocumentMutation, isPending: isDeletingDocument} = useDeleteDocument();
  const {mutateAsync: retryFinalSubmitMutation} = useRetryFinalSubmit();
  const {mutateAsync: retrySubmitForSignatureMutation} = useRetrySubmitForSignature();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({top: 0, left: 0});

  const documentActions = useMemo(() => {
    return {
      ...document.actions,
      canDelete: document.actions.canDelete,
    };
  }, [document.actions]);

  const hasAvailableActions = useMemo(() => {
    return Object.values(documentActions).some(action => action);
  }, [documentActions]);

  const onToggleMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const clientRect = event.currentTarget.getBoundingClientRect();

    setMenuPosition({top: clientRect.top + 30, left: clientRect.left - 15});
    setIsMenuVisible(menuIsVisible => !menuIsVisible);
  }, []);

  const onCloseMenu = useCallback(() => {
    setIsMenuVisible(false);
  }, []);

  const onResend = useCallback(async () => {
    navigate(`/app/form/${document.id}`, {state: {isResendingDocument: true}});

    onClick();
  }, [navigate, document.id, onClick]);

  const onStartEdition = useCallback(() => {
    setIsEditing(true);
  }, []);

  const onCancelEdition = useCallback(() => {
    setIsEditing(false);
  }, []);

  const onConfirmEdition = useCallback(async () => {
    try {
      await cancelSignatureRequestMutation(document.id);
      onCloseMenu();
    } finally {
      setIsEditing(false);
      onClick();
    }
  }, [cancelSignatureRequestMutation, document.id, onCloseMenu, onClick]);

  const onRetrySubmitForSignature = useCallback(async () => {
    try {
      await retrySubmitForSignatureMutation(document.id);
      onCloseMenu();
    } finally {
      onClick();
    }
  }, [document.id, onClick, onCloseMenu, retrySubmitForSignatureMutation]);

  const onRetryFinalSubmit = useCallback(async () => {
    try {
      await retryFinalSubmitMutation(document.id);
      onCloseMenu();
    } finally {
      onClick();
    }
  }, [document.id, onClick, onCloseMenu, retryFinalSubmitMutation]);

  const onStartDeletion = useCallback(() => {
    setIsDeleting(true);
  }, []);

  const onCancelDeletion = useCallback(() => {
    setIsDeleting(false);
  }, []);

  const onConfirmDeletion = useCallback(async () => {
    try {
      await deleteDocumentMutation(document.id);
      onCloseMenu();

      if (isCurrentDocumentRoute) {
        navigate(APP_PATH, {state: {isDeletedDocument: true}});
      }
    } finally {
      setIsDeleting(false);
    }
  }, [deleteDocumentMutation, document.id, isCurrentDocumentRoute, navigate, onCloseMenu]);

  const deleteConfirmDialogActions = useConfirmDialogActions({
    onConfirm: onConfirmDeletion,
    confirmButtonTestId: 'dialog-delete-confirm-button',
    onCancel: onCancelDeletion,
    cancelButtonTestId: 'dialog-delete-cancel-button',
  });

  const editConfirmDialogActions = useConfirmDialogActions({
    onConfirm: onConfirmEdition,
    confirmButtonTestId: 'dialog-edit-confirm-button',
    onCancel: onCancelEdition,
    cancelButtonTestId: 'dialog-edit-cancel-button',
  });

  return (
    <>
      <li className="relative w-full px-3 py-1">
        <div className="group">
          {linksDisabled ? (
            <div
              className="peer relative flex items-center justify-between rounded-lg p-3"
              data-testid={`document-link-${document.id}`}
            >
              <ItemContent document={document} showVersion={showVersion} />
            </div>
          ) : (
            <Link
              className="peer relative flex items-center justify-between rounded-lg p-3 transition-colors group-hover:bg-greige"
              data-testid={`document-link-${document.id}`}
              onClick={onClick}
              to={`form/${document.id}`}
              aria-label={t('components.document-menu.document-list.item.open-accessibility-link', {
                documentName: document.name,
              })}
              trackingKey={WEB_CLICK_TRACKING_KEY.OPEN_DOCUMENT}
            >
              <ItemContent document={document} showVersion={showVersion} />
            </Link>
          )}

          {hasAvailableActions && (
            <TrackedButton
              data-testid="menu-toggle"
              type="button"
              onClick={onToggleMenu}
              className="absolute right-6 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border border-greige-925 bg-white opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100 peer-focus:opacity-100"
              aria-label={t('components.document-menu.document-list.item.open-menu-accessibility-button')}
              trackingKey={WEB_CLICK_TRACKING_KEY.OPEN_DOCUMENT_OPTIONS}
              trackingAs={TrackingClickPrefix.CONTENT}
            >
              <HorizontalDotsIcon className="size-4 fill-grey" />
            </TrackedButton>
          )}

          {isMenuVisible && (
            <div className="fixed left-0 top-0 z-50 h-screen w-screen bg-black/5">
              <PopupMenu
                style={{top: `${menuPosition.top}px`, left: `${menuPosition.left}px`}}
                className="absolute w-36 min-w-0 rounded-sm p-0"
                onClose={onCloseMenu}
              >
                {documentActions.canRetrySendForSignature && (
                  <Button
                    theme={BUTTON_THEME.STYLELESS}
                    variant={BUTTON_VARIANT.FULL_WIDTH}
                    icon={<Arrow className="size-4 fill-violet" />}
                    label={
                      <div className="text-sm text-black">{t('components.document-menu.document-list.item.retry')}</div>
                    }
                    className="justify-end rounded-none hover:bg-purple-100"
                    onClick={onRetrySubmitForSignature}
                    trackingKey={WEB_CLICK_TRACKING_KEY.RETRY_DOCUMENT}
                  />
                )}

                {documentActions.canReinitializeSignature && (
                  <Button
                    theme={BUTTON_THEME.STYLELESS}
                    variant={BUTTON_VARIANT.FULL_WIDTH}
                    icon={<Arrow className="size-4 fill-violet" />}
                    label={
                      <div className="text-sm text-black">
                        {t('components.document-menu.document-list.item.resend')}
                      </div>
                    }
                    className="justify-end rounded-none hover:bg-purple-100"
                    onClick={onResend}
                    trackingKey={WEB_CLICK_TRACKING_KEY.RESEND_DOCUMENT}
                  />
                )}

                {documentActions.canModifySignature && (
                  <Button
                    theme={BUTTON_THEME.STYLELESS}
                    variant={BUTTON_VARIANT.FULL_WIDTH}
                    icon={<PencilOutline className="size-4 fill-violet" />}
                    label={
                      <div className="text-sm text-black">{t('components.document-menu.document-list.item.edit')}</div>
                    }
                    className="justify-end rounded-none hover:bg-purple-100"
                    onClick={onStartEdition}
                    trackingKey={WEB_CLICK_TRACKING_KEY.EDIT_DOCUMENT}
                  />
                )}

                {documentActions.canRetryFinalSubmit && (
                  <Button
                    theme={BUTTON_THEME.STYLELESS}
                    variant={BUTTON_VARIANT.FULL_WIDTH}
                    icon={<Arrow className="size-4 fill-violet" />}
                    label={
                      <div className="text-sm text-black">
                        {t('components.document-menu.document-list.item.retry-final-submit')}
                      </div>
                    }
                    className="justify-end rounded-none hover:bg-purple-100"
                    onClick={onRetryFinalSubmit}
                    trackingKey={WEB_CLICK_TRACKING_KEY.RETRY_FINAL_SUBMIT}
                  />
                )}

                {documentActions.canDelete && (
                  <Button
                    theme={BUTTON_THEME.STYLELESS}
                    variant={BUTTON_VARIANT.FULL_WIDTH}
                    icon={<TrashCan className="size-4 fill-violet" />}
                    label={
                      <div className="text-sm text-black">
                        {t('components.document-menu.document-list.item.delete')}
                      </div>
                    }
                    className="justify-end rounded-none hover:bg-purple-100"
                    onClick={onStartDeletion}
                    isLoading={isDeletingDocument}
                    data-testid={`menu-delete-button-${document.id}`}
                    trackingKey={WEB_CLICK_TRACKING_KEY.DELETE_DOCUMENT}
                  />
                )}
              </PopupMenu>
            </div>
          )}
        </div>
      </li>

      <ConfirmDialog
        isVisible={isDeleting}
        title={t('components.document-menu.document-list.item.delete-confirm.title')}
        text={t('components.document-menu.document-list.item.delete-confirm.text')}
        actions={deleteConfirmDialogActions}
      />

      <ConfirmDialog
        isVisible={isEditing}
        title={t('components.document-menu.document-list.item.edit-confirm.title')}
        text={t('components.document-menu.document-list.item.edit-confirm.text')}
        actions={editConfirmDialogActions}
      />
    </>
  );
};

export default DocumentListItem;
